// src/pages/Accounts/ExpenseRecognitionList.js
import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Typography, Modal, App, Tooltip, Drawer, Grid} from "antd";

const { useBreakpoint } = Grid;
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  approveExpenseRecognition,
  fetchRecognitions,
  deleteRecognition,
} from "./expenseRecognitionsThunk";
import {
  resetRecognitions,
  setSelectedRecognition,
  setIsEditRecognition,
  setRefreshList,
} from "./expenseRecognitionsSlice";
import dayjs from "dayjs";
import { formatDateOnly, getAccessType } from "../../lib/functions";
import { DeleteOutlined } from "@ant-design/icons";
import { useIsAdminUser } from "../../lib/accessList";
import { useTxLock } from "../../hooks/useTxLock";

const { Title } = Typography;

const ExpenseRecognitionList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list, loading, refreshList } = useSelector(
    (state) => state.expenseRecognitions
  );
  const isAdminUser = useIsAdminUser();
  const screens = useBreakpoint();
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  let [access_type, access_type_set] = useState("");
  useEffect(() => {
    const access = getAccessType("expense_recognition_entry");
    access_type_set(access);
  }, []);
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  useEffect(() => {
    dispatch(resetRecognitions());
    dispatch(fetchRecognitions());
  }, [dispatch]);

  useEffect(() => {
    if (refreshList) {
      dispatch(fetchRecognitions());
      dispatch(setRefreshList(false));
    }
  }, [refreshList, dispatch]);

  const { modal } = App.useApp();

  const handleDelete = async (exp_id) => {
    try {
      await dispatch(deleteRecognition(exp_id)).unwrap();
      modal.success({
        title: "Expense recognition entry deleted successfully",
      });
      dispatch(fetchRecognitions());
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Recognition Date",
      dataIndex: "created_date",
      render: (value) => (value ? formatDateOnly(value) : "-"),
    },
    {
      title: "Recognition Total",
      dataIndex: "total_amount",
      align: "right",
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Created By",
      dataIndex: "full_name",
    },
    ...(access_type === "WRITE"
      ? [
        {
          title: "Status",
          dataIndex: "status",
          render: (_, record) => {
            if (record.status === "a") {
              return (
                <span style={{ color: "green", fontWeight: 500 }}>
                  Approved
                </span>
              );
            }

            // status === "p" (Pending)
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => {
                  Modal.confirm({
                    title: "Are you sure approve this?",
                    okText: "Yes",
                    cancelText: "No",
                    onOk: async () => {
                      try {
                        await dispatch(
                          approveExpenseRecognition(record.recog_id)
                        ).unwrap();
                        modal.success({
                          title: "Recognition approved successfully",
                        });
                        dispatch(fetchRecognitions()); // reload list
                      } catch (err) {
                        modal.error({
                          title: err.message || "Failed to approve",
                        });
                      }
                    },
                  });
                }}
              >
                <CheckCircleOutlined
                  style={{ color: "green", fontSize: 18, marginRight: 6 }}
                />
                <span style={{ color: "#444" }}>Pending</span>
              </div>
            );
          },
        },
   {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <>
        <Tooltip
          title={
            record.status === "a"
              ? "Approved entries cannot be edited"
              : txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            disabled={
              !(isAdminUser || canEdit) ||
              record.status === "a" ||
              txLocked
            }
            onClick={() => {
              if (record.status === "a" || txLocked) return;

              dispatch(setSelectedRecognition(record));
              dispatch(setIsEditRecognition(true));

              navigate(`/accounts/expense-recognitions/add`);
            }}
            icon={<EditOutlined />}
          />
        </Tooltip>

        <Tooltip
          title={
            record.status === "a"
              ? "Approved entries cannot be deleted"
              : txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Button
            type="link"
            danger
            disabled={
              !(isAdminUser || canDelete) ||
              record.status === "a" ||
              txLocked
            }
            icon={<DeleteOutlined />}
            onClick={() => {
              if (record.status === "a" || txLocked) return;

              modal.confirm({
                title: "Are you sure?",
                content: `This will delete Expense Recognition entry with amount: ${record.total_amount}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () => handleDelete(record.recog_id),
              });
            }}
          />
        </Tooltip>
      </>
    );
  },
}
      ]
      : []),
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="expense-recognition-list-back-button"
        />
        <Title
          level={3}
          style={{
            margin: 0,
            wordBreak: "break-word",
            fontSize: screens.md ? undefined : "18px",
          }}
        >
          Expense Recognition List
        </Title>

        <div style={{ marginLeft: "auto" }}>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      </div>
      <Row justify="end" style={{ marginBottom: 32, marginTop: 32 }}>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedRecognition(null));
                  dispatch(setIsEditRecognition(false));
                  navigate("/accounts/expense-recognitions/add");
                }}
                data-testid="btn-add-exp-recog"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={list}
        rowKey={(r) => r.recog_id || r.exp_id}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: list.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
        size="middle"
        scroll={{ x: "max-content" }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
      <Drawer
        title="Expense Recognition Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Expense Recognition Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "12px",
            }}
          >
            The Expense Recognition module is used to recognize and approve
            recorded expenses within the ERP accounting system. It helps ensure
            proper expense verification, approval workflow, and financial tracking.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View expense recognition records</li>
              <li>Use <strong>Add New</strong> to create recognition entries</li>
              <li>Edit pending recognition entries</li>
              <li>Delete pending entries if permitted</li>
              <li>Approve expense recognitions</li>
              <li>Track recognition totals and descriptions</li>
              <li>Monitor approval statuses and accounting workflow</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Approved entries cannot be edited or deleted.</li>
              <li>Expense approval affects accounting records immediately.</li>
              <li>Status indicates whether recognition is pending or approved.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Recognition history is maintained for auditing and reports.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ExpenseRecognitionList;
